import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Users, FileText, Clock, AlertCircle, ChevronRight, Calendar, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MeetingRecordDetailModal } from "@/components/governance/MeetingRecordDetailModal";
import { ResolutionDetailModal } from "@/components/governance/ResolutionDetailModal";
import { CollapsibleRecordRow, CollapsibleRecordList } from "@/components/governance/CollapsibleRecordRow";
import { JournalEntryLogSection } from "@/components/governance/JournalEntryLogSection";
import { format } from "date-fns";
import { UNAHeaderCard } from "@/components/dashboard/UNAHeaderCard";
import { getUserTier } from "@/lib/tierUtils";
import { SectionExplainerVideo } from "@/components/common/SectionExplainerVideo";

const UNAGovernance = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null);
  const [selectedResolution, setSelectedResolution] = useState<any>(null);
  const [downloadingResolutionId, setDownloadingResolutionId] = useState<string | null>(null);
  const [downloadingMeetingId, setDownloadingMeetingId] = useState<string | null>(null);

  const downloadFromDocRow = async (
    doc: any | null,
    label: string,
    fallbackFilename: string,
  ) => {
    if (!doc) {
      toast.error("No document record found");
      return;
    }
    let url: string | null = doc?.signed_file_url || doc?.file_url || null;
    let filePath: string | null = doc?.file_path || null;

    // Prepare PDF on demand when finalized doc has no file yet
    const canPrepare =
      !!doc?.id &&
      (!!doc?.custom_content ||
        (doc?.document_type === "formal_meeting_minutes" && !!doc?.task_id));
    if (!url && !filePath && canPrepare) {
      toast.info(`${label} just needs its PDF prepared. Preparing now…`);
      const { regenerateDocumentPdf } = await import("@/lib/regeneratePdf");
      const res = await regenerateDocumentPdf(doc.id);
      if (res.error) {
        toast.error(res.error);
        return;
      }
      if (res.file_path) filePath = res.file_path;
    }

    if (!url && !filePath) {
      toast.error("No document available for download");
      return;
    }

    if (filePath) {
      const { data: signed } = await supabase.storage
        .from("documents")
        .createSignedUrl(filePath, 300, { download: fallbackFilename });
      url = signed?.signedUrl || url;
    }
    if (!url) {
      toast.error("Could not generate a download link");
      return;
    }
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fallbackFilename;
    link.click();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleResolutionDownload = async (resolutionId: string, resolutionTitle: string) => {
    setDownloadingResolutionId(resolutionId);
    try {
      const { data: doc } = await supabase
        .from('generated_documents' as any)
        .select('id, file_url, signed_file_url, file_path, document_name, custom_content, document_type, task_id')
        .eq('resolution_id', resolutionId)
        .maybeSingle();
      await downloadFromDocRow(
        doc as any,
        "This resolution",
        `${resolutionTitle.replace(/[^a-z0-9]/gi, '-')}.pdf`,
      );
    } catch (error) {
      toast.error("Failed to download document");
    } finally {
      setDownloadingResolutionId(null);
    }
  };

  const handleMeetingDownload = async (meetingId: string, meetingTitle: string) => {
    setDownloadingMeetingId(meetingId);
    try {
      const { data: doc } = await supabase
        .from('generated_documents' as any)
        .select('id, file_url, signed_file_url, file_path, document_name, custom_content, document_type, task_id')
        .eq('meeting_id', meetingId)
        .maybeSingle();
      await downloadFromDocRow(
        doc as any,
        "This meeting document",
        `${meetingTitle.replace(/[^a-z0-9]/gi, '-')}.pdf`,
      );
    } catch (error) {
      toast.error("Failed to download document");
    } finally {
      setDownloadingMeetingId(null);
    }
  };


  // Fetch UNA membership for current user - check Secretary Treasurer first, then una_members
  const { data: membership, isLoading: membershipLoading } = useQuery({
    queryKey: ["una-membership", user?.id],
    queryFn: async () => {
      // First check if user is a Secretary Treasurer
      const { data: unaAsTS, error: tsError } = await supabase
        .from("unas")
        .select("*")
        .eq("treasury_secretary_id", user?.id)
        .maybeSingle();

      if (unaAsTS) {
        return {
          una_id: unaAsTS.id,
          user_id: user?.id,
          member_role: 'treasury_secretary',
          status: 'active',
          una: unaAsTS
        };
      }

      // Fall back to checking una_members table
      const { data, error } = await supabase
        .from("una_members")
        .select("*, una:una_id(*)")
        .eq("user_id", user?.id)
        .eq("status", "active")
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch member count
  const { data: memberCount = 0 } = useQuery({
    queryKey: ["member-count", membership?.una_id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("una_members")
        .select("*", { count: "exact", head: true })
        .eq("una_id", membership?.una_id);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!membership?.una_id,
  });

  // Fetch document count
  const { data: documentCount = 0 } = useQuery({
    queryKey: ["document-count", membership?.una_id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("governance_documents")
        .select("*", { count: "exact", head: true })
        .eq("una_id", membership?.una_id);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!membership?.una_id,
  });

  // Fetch pending invitations count
  const { data: pendingInvitationsCount = 0 } = useQuery({
    queryKey: ["pending-invitations", membership?.una_id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("member_invitations")
        .select("*", { count: "exact", head: true })
        .eq("una_id", membership?.una_id)
        .eq("status", "pending");

      if (error) throw error;
      return count || 0;
    },
    enabled: !!membership?.una_id,
  });

  // Fetch pending signatures count
  const { data: pendingSignaturesCount = 0 } = useQuery({
    queryKey: ["pending-signatures", membership?.una_id],
    queryFn: async () => {
      const { data: requests, error } = await supabase
        .from("signature_requests")
        .select("id")
        .eq("una_id", membership?.una_id)
        .eq("status", "pending");

      if (error) throw error;
      return requests?.length || 0;
    },
    enabled: !!membership?.una_id,
  });

  // Fetch recent meetings (including prep and finalized)
  const { data: recentMeetings = [] } = useQuery({
    queryKey: ['recent-meetings', membership?.una_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('meeting_minutes')
        .select(`
          id,
          meeting_type,
          meeting_date,
          meeting_purpose,
          meeting_location,
          meeting_start_time,
          status,
          task_id,
          actual_attendees,
          expected_attendees,
          special_guests,
          created_at
        `)
        .eq('una_id', membership?.una_id)
        .in('status', ['finalized', 'prep', 'archived', 'reviewing'])
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!membership?.una_id
  });

  // Fetch resolutions
  const { data: resolutions = [] } = useQuery({
    queryKey: ['una_resolutions', membership?.una_id],
    queryFn: async () => {
      if (!membership?.una_id) return [];
      
      const { data, error } = await supabase
        .from('una_resolutions' as any)
        .select('*')
        .eq('una_id', membership?.una_id)
        .order('resolution_date', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!membership?.una_id,
  });

  // Fetch constitution document
  const { data: constitutionDoc, refetch: refetchConstitution } = useQuery({
    queryKey: ["constitution-doc", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_journey_documents")
        .select("id, file_url, file_path, file_size, document_name")
        .eq("user_id", user?.id)
        .eq("document_type", "constitution")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch user tier
  const { data: userTier } = useQuery({
    queryKey: ["user-tier", user?.id],
    queryFn: () => getUserTier(user!.id),
    enabled: !!user?.id,
  });

  const totalPendingActions = pendingInvitationsCount + pendingSignaturesCount;
  const unaName = membership?.una?.name || "Your UNA";

  if (membershipLoading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-32 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!membership) {
    return (
      <MainLayout>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You are not currently a member of any UNA. Please contact your administrator.
          </AlertDescription>
        </Alert>
      </MainLayout>
    );
  }

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Governance': 'bg-blue-100 text-blue-800',
      'Financial': 'bg-green-100 text-green-800',
      'Asset Management': 'bg-purple-100 text-purple-800',
      'Membership': 'bg-yellow-100 text-yellow-800',
      'Operations': 'bg-slate-100 text-slate-800',
      'Policy': 'bg-orange-100 text-orange-800',
      'Appointment': 'bg-red-100 text-red-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <MainLayout>
      <div className="container max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Section Explainer Video */}
        <SectionExplainerVideo
          videoUrl="https://vimeo.com/1152239219"
          sectionKey="governance"
          title="Governance Overview"
          description="Learn how to manage your UNA governance, members, and documents"
        />

        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">UNA Governance</h1>
          <p className="text-muted-foreground mt-2">
            Manage your UNA members, documents, and governance activities
          </p>
        </div>

        {/* UNA Header Card with Constitution */}
        <UNAHeaderCard
          unaName={unaName}
          constitutionDate={membership?.una?.constitution_date}
          treasurySecretaryName={user?.email || "Secretary Treasurer"}
          tier={userTier || 'Premium'}
          constitutionDocument={constitutionDoc}
          onConstitutionUpload={refetchConstitution}
          userId={user?.id}
        />

        {/* Main Grid - 2 Core Areas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Members Management Card */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/una-members")}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <Users className="h-8 w-8 text-primary" />
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
              <CardTitle className="mt-4">Members Management</CardTitle>
              <CardDescription>Manage your UNA membership roster</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Members</span>
                  <Badge variant="secondary">{memberCount}</Badge>
                </div>
                <Button variant="outline" className="w-full mt-4" onClick={(e) => {
                  e.stopPropagation();
                  navigate("/una-members");
                }}>
                  View All Members
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Governance Documents Card */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/governance-documents")}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <FileText className="h-8 w-8 text-primary" />
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
              <CardTitle className="mt-4">Document Repository</CardTitle>
              <CardDescription>Access governance documents</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Documents</span>
                  <Badge variant="secondary">{documentCount}</Badge>
                </div>
                <Button variant="outline" className="w-full mt-4" onClick={(e) => {
                  e.stopPropagation();
                  navigate("/governance-documents");
                }}>
                  View Repository
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Meeting Records Section - Simplified */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle>Meeting Records</CardTitle>
                {recentMeetings.length > 0 && (
                  <Badge variant="secondary">{recentMeetings.length}</Badge>
                )}
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/workflows?filter=meeting')}
              >
                Record New Meeting
              </Button>
            </div>
            <CardDescription>
              Official certified minutes from UNA meetings
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentMeetings.length > 0 ? (
              <CollapsibleRecordList maxVisible={3}>
                {recentMeetings.map((meeting: any) => {
                  const meetingTypeFormatted = meeting.meeting_type?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Meeting';
                  const attendeeCount = meeting.status === 'prep' 
                    ? (Array.isArray(meeting.expected_attendees) ? meeting.expected_attendees.length : 0)
                    : (Array.isArray(meeting.actual_attendees) ? meeting.actual_attendees.length : 0);
                  
                  return (
                  <CollapsibleRecordRow
                      key={meeting.id}
                      id={meeting.id}
                      title={meetingTypeFormatted}
                      date={meeting.created_at}
                      type="meeting"
                      badge={{
                        label: meeting.status === 'prep' ? 'Meeting Prep' : 'Recorded',
                        className: meeting.status === 'prep' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-green-100 text-green-800',
                      }}
                      subtitle={`${attendeeCount} attendees • ${meeting.meeting_purpose || 'Board Meeting'}`}
                      onClick={() => setSelectedMeeting(meeting)}
                      actions={
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMeetingDownload(meeting.id, meetingTypeFormatted);
                          }}
                          disabled={downloadingMeetingId === meeting.id}
                        >
                          {downloadingMeetingId === meeting.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                      }
                      expandedContent={
                        <div className="space-y-2 text-sm">
                          {meeting.meeting_purpose && (
                            <p><span className="text-muted-foreground">Purpose:</span> {meeting.meeting_purpose}</p>
                          )}
                          {meeting.meeting_location && (
                            <p><span className="text-muted-foreground">Location:</span> {meeting.meeting_location}</p>
                          )}
                          <p><span className="text-muted-foreground">Attendees:</span> {attendeeCount}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedMeeting(meeting);
                            }}
                          >
                            View Details
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      }
                    />
                  );
                })}
              </CollapsibleRecordList>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No meeting records yet. Record your first meeting to get started.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Resolutions Section - Simplified */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle>Resolutions & Actions</CardTitle>
                {resolutions.length > 0 && (
                  <Badge variant="secondary">{resolutions.length}</Badge>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/workflows?filter=resolution')}
              >
                New Resolution
              </Button>
            </div>
            <CardDescription>
              Official resolutions documenting UNA decisions and governance actions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {resolutions.length > 0 ? (
              <CollapsibleRecordList maxVisible={3}>
                {resolutions.map((resolution: any) => (
                  <CollapsibleRecordRow
                    key={resolution.id}
                    id={resolution.id}
                    title={resolution.resolution_title}
                    date={resolution.resolution_date}
                    type="resolution"
                    badge={{
                      label: resolution.resolution_category,
                      className: getCategoryColor(resolution.resolution_category),
                    }}
                    subtitle={`${resolution.whereas_clauses?.length || 0} WHEREAS • ${resolution.resolved_clauses?.length || 0} RESOLVED`}
                    onClick={() => setSelectedResolution(resolution)}
                    actions={
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResolutionDownload(resolution.id, resolution.resolution_title);
                        }}
                        disabled={downloadingResolutionId === resolution.id}
                      >
                        {downloadingResolutionId === resolution.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    }
                    expandedContent={
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="text-muted-foreground">Adopted:</span>{' '}
                          {format(new Date(resolution.resolution_date), 'MMMM d, yyyy')}
                        </p>
                        {resolution.filed_date && (
                          <p>
                            <span className="text-muted-foreground">Filed:</span>{' '}
                            {format(new Date(resolution.filed_date), 'MMMM d, yyyy')}
                          </p>
                        )}
                        {resolution.implementation_notes && (
                          <p className="text-muted-foreground italic">
                            {resolution.implementation_notes.substring(0, 100)}
                            {resolution.implementation_notes.length > 100 ? '...' : ''}
                          </p>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedResolution(resolution);
                          }}
                        >
                          View Details
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    }
                  />
                ))}
              </CollapsibleRecordList>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No resolutions filed yet</p>
                <p className="text-sm mt-2">Use the "Writing a Resolution" workflow to create your first resolution</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Journal Entry Log Section */}
        <JournalEntryLogSection unaId={membership?.una_id} maxVisible={5} />

        {/* Pending Actions Banner */}
        {totalPendingActions > 0 && (
          <Card className="border-orange-200 bg-orange-50/50">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-600" />
                <CardTitle className="text-lg">Pending Actions Requiring Attention</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pendingInvitationsCount > 0 && (
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm">
                      {pendingInvitationsCount} pending member invitation{pendingInvitationsCount !== 1 ? 's' : ''}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate("/una-members")}
                    >
                      Review
                    </Button>
                  </div>
                )}
                {pendingSignaturesCount > 0 && (
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm">
                      {pendingSignaturesCount} document{pendingSignaturesCount !== 1 ? 's' : ''} awaiting signature
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate("/governance/tasks")}
                    >
                      Review
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Meeting Record Detail Modal */}
        <MeetingRecordDetailModal
          meeting={selectedMeeting}
          open={!!selectedMeeting}
          onOpenChange={(open) => !open && setSelectedMeeting(null)}
          unaId={membership?.una_id || ''}
        />

        {/* Resolution Detail Modal */}
        <ResolutionDetailModal
          resolution={selectedResolution}
          open={!!selectedResolution}
          onOpenChange={(open) => !open && setSelectedResolution(null)}
          unaId={membership?.una_id || ''}
        />
      </div>
    </MainLayout>
  );
};

export default UNAGovernance;
